import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-gray-800 rounded-lg p-8 shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-yellow-400 mb-4">Privacy Policy</h1>
          <p className="text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="space-y-6 text-gray-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Introduction</h2>
            <p className="leading-relaxed">
              CS2 Lineups ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Information We Collect</h2>
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-yellow-400">Google Analytics</h3>
              <p className="leading-relaxed">
                We use Google Analytics to understand how visitors interact with our website. Google Analytics collects the following information:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Pages visited and time spent on each page</li>
                <li>Your device type and browser information</li>
                <li>General location data (country/city level)</li>
                <li>How you found our website (search terms, referring sites)</li>
                <li>User interactions (clicks, scrolls, etc.)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">How We Use Your Information</h2>
            <p className="leading-relaxed mb-3">
              We use the collected information to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Improve our website and user experience</li>
              <li>Understand which CS2 lineups are most popular</li>
              <li>Analyze website performance and fix issues</li>
              <li>Make informed decisions about content and features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Google Analytics and Cookies</h2>
            <div className="space-y-3">
              <p className="leading-relaxed">
                Google Analytics uses cookies and similar technologies to collect information about your browsing activity. These cookies help us understand:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>How long you stay on our website</li>
                <li>Which pages you visit most frequently</li>
                <li>Whether you're a new or returning visitor</li>
                <li>Your general geographic location</li>
              </ul>
              <p className="leading-relaxed">
                You can control cookie settings through your browser preferences. However, disabling cookies may affect your experience on our website.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data Sharing</h2>
            <p className="leading-relaxed">
              We do not sell, trade, or otherwise transfer your personal information to third parties. Google Analytics data is processed by Google according to their Privacy Policy. We only use this data in aggregate form to improve our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data Security</h2>
            <p className="leading-relaxed">
              We implement appropriate security measures to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Your Rights</h2>
            <p className="leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Opt out of Google Analytics tracking</li>
              <li>Delete cookies through your browser settings</li>
              <li>Contact us with privacy-related questions</li>
              <li>Request information about data we collect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Opting Out of Google Analytics</h2>
            <p className="leading-relaxed">
              You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on, available at: 
              <a 
                href="https://tools.google.com/dlpage/gaoptout" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-400 hover:text-yellow-300 underline ml-1"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Children's Privacy</h2>
            <p className="leading-relaxed">
              Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us through our website or social media channels.
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 